import { customElement, getCurrentElement, noShadowDOM } from 'solid-element'
import type { Component } from 'solid-js'
import { StoryDefinition, StoryMetadata } from './stories'
import { render as renderSolid } from 'solid-js/web'
import { toKebabCase } from 'common'

export function register<T extends object = {}>(name: string, module: string, defaultProps: T, component: Component<T>) {
    window.solidComponents = window.solidComponents || {}

    window.solidComponents[name] = {
        name,
        module,
        component,
        defaultProps,
        metadata: undefined,
        stories: {}
    }

    customElement(name, defaultProps, (props: T) => {
        noShadowDOM()
        const wrapper = getCurrentElement()
        for (const key in defaultProps) {
            if (key.startsWith('on') && typeof defaultProps[key] === 'function') {
                const eventName = toKebabCase(key.substring(2))
                const originalHandler = props[key]
                const overridenHandler = (...args: any[]) => {
                    const event = new CustomEvent(eventName, {
                        detail: args,
                        bubbles: true,
                        cancelable: true,
                        composed: true,
                    })

                    wrapper.dispatchEvent(event)

                    if (typeof originalHandler === 'function') {
                        originalHandler?.(...args)
                    } else {
                        // Maybe support string events. Probably not raw function execution, but a string event name
                        console.warn(`Event ${eventName} is not a function, but a string. This is not supported.`)
                    }
                }

                props[key] = overridenHandler as any
            }

        }

        return component(props as T)
    })
}

export function render<T extends object = {}>(component: Component<T>, props: T, element: HTMLElement) {
    renderSolid(() => component(props), element || document.body)
}

export interface ComponentDefinition {
    name: string,
    component: Component<any>,
    defaultProps: any,
    module: string,
    metadata?: () => StoryMetadata<Component<any>>
    stories: Record<string, () => StoryDefinition<Component>>,
}

declare global {
    interface Window {
        solidComponents: Record<string, ComponentDefinition>
    }
}